import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '@/context/ThemeContext';
import PageSEO from '@/components/PageSEO';
import NewsletterSignup from '@/components/NewsletterSignup';
import { ArrowRight, Rss, Clock, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const BlogHeader = ({ isDark }) => (
  <header className={`sticky top-0 z-40 border-b backdrop-blur-xl ${isDark ? 'border-zinc-800 bg-zinc-950/80' : 'border-zinc-200 bg-white/80'}`}>
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
      <Link to="/" data-testid="blog-home-link" className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold"
          style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>{'\u03A9'}</div>
        <span className="font-bold tracking-tight">OMNIVIEW</span>
      </Link>
      <div className="flex items-center gap-4">
        <a data-testid="blog-rss-link" href={`${API}/api/rss.xml`} target="_blank" rel="noreferrer"
          className={`text-xs flex items-center gap-1 ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-500 hover:text-zinc-800'}`}>
          <Rss size={13} /> RSS
        </a>
        <Link to="/auth" data-testid="blog-cta-trial"
          className="px-4 py-2 rounded-md text-sm font-bold text-white transition-all hover:shadow-lg hover:shadow-blue-500/20"
          style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>Start free trial</Link>
      </div>
    </div>
  </header>
);

export default function BlogPage() {
  const { isDark } = useTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/blog/posts`)
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950 text-zinc-100' : 'bg-white text-zinc-900'}`}>
      <PageSEO title="Blog — Marketing Intelligence"
        description="Cross-channel marketing intelligence, revenue leakage, quick-commerce and agency operations. Written by the OmniView team."
        keywords="marketing intelligence blog, revenue leakage, roas, quick commerce, agency operations" />
      <BlogHeader isDark={isDark} />
      <main id="main" className="max-w-5xl mx-auto px-4 sm:px-8 py-16">
        <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#00D4FF' }}>The OmniView Journal</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-5 max-w-3xl">
          Where marketing money actually goes.
        </h1>
        <p className={`text-base max-w-2xl mb-14 leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
          Field notes on reconciling ad platforms, finding revenue leakage, running quick-commerce
          availability and automating agency operations without losing the plot.
        </p>

        {loading ? (
          <div data-testid="blog-loading" className="flex items-center gap-2 text-sm">
            <Loader2 size={16} className="animate-spin" /> Loading posts…
          </div>
        ) : (
          <div data-testid="blog-list" className="space-y-4">
            {posts.map((p, i) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                className={`block group rounded-xl border p-6 sm:p-8 transition-all animate-fadeUp
                  ${isDark ? 'border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/60' : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'}`}
                style={{ animationDelay: `${i * 70}ms` }}>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                    style={{ background: 'rgba(0,212,255,.1)', color: '#0091b3' }}>{p.category}</span>
                  <span className={`text-xs ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>{p.date}</span>
                  <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                    <Clock size={11} /> {p.read_minutes} min
                  </span>
                </div>
                <h2 className="text-base md:text-lg font-bold mb-2 group-hover:underline decoration-1 underline-offset-4">{p.title}</h2>
                <p className={`text-sm leading-relaxed mb-4 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{p.description}</p>
                <span className="text-sm font-bold inline-flex items-center gap-1" style={{ color: '#002FA7' }}>
                  Read <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
            {!posts.length && <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>No posts yet.</p>}
          </div>
        )}

        <div className={`mt-16 rounded-xl border p-8 ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`}>
          <NewsletterSignup isDark={isDark} source="blog_index" />
        </div>
      </main>
    </div>
  );
}
